/** Interpolação suave de Hermite entre e0 e e1, com saída em [0, 1]. */
export function smoothstep(e0: number, e1: number, x: number): number {
  const t = (x - e0) / (e1 - e0);
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}

/**
 * Curva monotônica por partes com tangentes de Fritsch–Carlson: passa pelos nós, nunca
 * oscila entre eles e só usa polinômios (determinística). Fora do domínio, satura.
 */
export class MonotoneCurve {
  private readonly xs: Float64Array;
  private readonly ys: Float64Array;
  private readonly ms: Float64Array;

  constructor(points: readonly (readonly [number, number])[]) {
    const n = points.length;
    if (n < 2) throw new RangeError("curva precisa de ao menos 2 pontos");
    this.xs = new Float64Array(n);
    this.ys = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const [x, y] = points[i] as readonly [number, number];
      if (i > 0 && x <= (this.xs[i - 1] as number)) throw new RangeError("x da curva deve ser crescente");
      if (i > 0 && y < (this.ys[i - 1] as number)) throw new RangeError("y da curva deve ser não decrescente");
      this.xs[i] = x;
      this.ys[i] = y;
    }
    const delta = new Float64Array(n - 1);
    for (let i = 0; i < n - 1; i++) {
      delta[i] = ((this.ys[i + 1] as number) - (this.ys[i] as number)) / ((this.xs[i + 1] as number) - (this.xs[i] as number));
    }
    const m = new Float64Array(n);
    m[0] = delta[0] as number;
    m[n - 1] = delta[n - 2] as number;
    for (let i = 1; i < n - 1; i++) {
      const d0 = delta[i - 1] as number;
      const d1 = delta[i] as number;
      m[i] = d0 * d1 <= 0 ? 0 : (d0 + d1) / 2;
    }
    for (let i = 0; i < n - 1; i++) {
      const d = delta[i] as number;
      if (d === 0) {
        m[i] = 0;
        m[i + 1] = 0;
        continue;
      }
      const a = (m[i] as number) / d;
      const b = (m[i + 1] as number) / d;
      const s = a * a + b * b;
      if (s > 9) {
        const tau = 3 / Math.sqrt(s);
        m[i] = tau * a * d;
        m[i + 1] = tau * b * d;
      }
    }
    this.ms = m;
  }

  evaluate(x: number): number {
    const xs = this.xs;
    const n = xs.length;
    if (x <= (xs[0] as number)) return this.ys[0] as number;
    if (x >= (xs[n - 1] as number)) return this.ys[n - 1] as number;
    let i = 0;
    while (x > (xs[i + 1] as number)) i++;
    const x0 = xs[i] as number;
    const h = (xs[i + 1] as number) - x0;
    const t = (x - x0) / h;
    const t2 = t * t;
    const t3 = t2 * t;
    return (
      (2 * t3 - 3 * t2 + 1) * (this.ys[i] as number) +
      (t3 - 2 * t2 + t) * h * (this.ms[i] as number) +
      (-2 * t3 + 3 * t2) * (this.ys[i + 1] as number) +
      (t3 - t2) * h * (this.ms[i + 1] as number)
    );
  }
}
